import { Pencil } from "lucide-react";
import { Input } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";

// Deal Pipeline spec §3.1 — read display + edit affordance for SSN, EIN,
// bank routing/account numbers. The masked value (e.g. "***-**-1234") is
// computed server-side and arrives on Read DTOs already obscured. The edit
// form takes a fresh plaintext value; we never echo or log the cleartext.

export interface MaskedSensitiveFieldProps {
  /** Server-supplied masked display value (e.g. "***-**-1234"). */
  masked?: string | null;
  /** Optional plaintext currently held in form state for an in-progress
   *  edit. The component never reflects this back to the read view. */
  editingValue?: string;
  /** Called when the user types a new value in edit mode. */
  onChange?: (next: string) => void;
  /** Whether to render in edit mode. When false, only the masked display
   *  + an "Edit" button are shown. */
  editing?: boolean;
  /** Called when the user clicks "Edit" to switch into edit mode. */
  onRequestEdit?: () => void;
  /** Visible label for screen readers and the inline header. */
  label: string;
  placeholder?: string;
  className?: string;
}

export function MaskedSensitiveField({
  masked,
  editingValue = "",
  onChange,
  editing = false,
  onRequestEdit,
  label,
  placeholder,
  className,
}: MaskedSensitiveFieldProps) {
  if (editing) {
    return (
      <Input
        type="text"
        autoComplete="off"
        // Phoenix sensitive-field UX: never default-show real input; user
        // must explicitly type the new value, and we never echo the
        // existing plaintext (we don't have it client-side anyway).
        value={editingValue}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder ?? `Enter new ${label.toLowerCase()}`}
        aria-label={`${label} (new value)`}
        className={className}
      />
    );
  }

  const displayValue = masked && masked.length > 0 ? masked : "—";

  return (
    <div className={cn("flex items-center gap-space-2", className)}>
      <span
        className="font-mono text-small text-onyx select-none"
        aria-label={`${label}, masked`}
        data-testid="masked-display"
      >
        {displayValue}
      </span>
      {onRequestEdit && (
        <button
          type="button"
          onClick={onRequestEdit}
          className="text-caption uppercase tracking-wide text-gold-shadow hover:text-gold inline-flex items-center gap-space-1"
          aria-label={`Edit ${label}`}
        >
          <Pencil size={12} strokeWidth={1.5} aria-hidden /> Edit
        </button>
      )}
    </div>
  );
}
